import { useEffect } from "react";

import {
  Button,
  Stack,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { usePostHog } from "posthog-js/react";

import { ClientAppEvent } from "constants/index";
import { useRoleContext, useWriterComponentState } from "hooks";

import { BANNER_CTA_BUTTON_SX, BANNER_TEXT_MIN_WIDTH } from "../../constants";

// "Public loads" rather than "quote" flatly: `_payment_method_missing_and_required`
// in quote/command_orchestrators/submit_quote.py returns False for partner quotes, so
// a carrier with no card can still quote a followed shipper's freight.
const BANNER_TEXT = "Add a payment method to quote public loads.";
const CTA_TOOLTIP =
  "Add a card in your Stripe billing portal so you can quote public loads. You are only charged when a booking is confirmed.";

export function PpbPaymentMethodBanner() {
  const showText = useMediaQuery(BANNER_TEXT_MIN_WIDTH);
  const { company } = useRoleContext();
  const posthog = usePostHog();
  const companyId = company?.id ?? null;

  const {
    loading,
    error,
    write: requestPaymentMethodUpdateSession,
  } = useWriterComponentState({
    errorMessage:
      "Could not open the payment portal. Please contact us if the issue persists.",
    writeConfig: {
      method: "post",
      // `source=banner` tells the endpoint to skip the ops notification that the
      // subscription page fires: this banner is on every page, so one ping per
      // click would be noise. PostHog carries the signal instead.
      url:
        companyId != null
          ? `/stripe/companies/ppb/${companyId}/payment-method-update?source=banner`
          : "/",
    },
  });

  useEffect(() => {
    if (companyId != null) {
      posthog?.capture(ClientAppEvent.PPB_PAYMENT_METHOD_BANNER_VIEWED);
    }
  }, [posthog, companyId]);

  if (companyId == null) {
    return null;
  }

  // The session is minted on click rather than on render: minting per page load
  // would create a bearer URL into the company's billing every time any carrier
  // opens any page.
  const openPaymentMethodPortal = () => {
    posthog?.capture(ClientAppEvent.PPB_PAYMENT_METHOD_BANNER_CLICKED);
    requestPaymentMethodUpdateSession<CreatePaymentMethodUpdateSessionResponse>(
      (response) => {
        window.location.href = response.url;
      },
    );
  };

  return (
    <Stack direction="row" alignItems="center" gap={2}>
      {showText && error == null && (
        <Typography variant="h6" color="white">
          {BANNER_TEXT}
        </Typography>
      )}
      {/*
      A failure is shown in white on the bar rather than through ErrorTypography,
      whose red does not read against the brand blue. It takes the copy's place
      instead of riding the tooltip so it lands without the carrier hovering, and
      it renders at every width, unlike the copy it replaces.
      */}
      {error != null && (
        <Typography variant="h6" color="white">
          {error}
        </Typography>
      )}
      {/*
      The span keeps the tooltip reachable while the button is disabled, since a
      disabled button emits no pointer events.
      */}
      <Tooltip title={CTA_TOOLTIP}>
        <span>
          <Button
            variant="contained"
            onClick={openPaymentMethodPortal}
            disabled={loading}
            sx={BANNER_CTA_BUTTON_SX}
          >
            Add Payment Method
          </Button>
        </span>
      </Tooltip>
    </Stack>
  );
}
